import { useChainId, useSwitchChain, useChains } from 'wagmi';
import { useState } from 'react';
import { Check, ChevronDown, AlertCircle } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export function NetworkSelector() {
    const chainId = useChainId();
    const { chains, switchChain, isPending } = useSwitchChain();
    const [isOpen, setIsOpen] = useState(false);

    // Find current chain
    const currentChain = chains.find(c => c.id === chainId);

    // Check if current network is supported
    const isUnsupported = !currentChain;

    const handleSwitch = (targetChainId: number) => {
        if (targetChainId === chainId) return;

        switchChain(
            { chainId: targetChainId },
            {
                onSuccess: () => {
                    toast.success(`Switched to ${chains.find(c => c.id === targetChainId)?.name}`);
                    setIsOpen(false);
                },
                onError: (error) => {
                    // User rejected
                    if (error.message.includes('User rejected') || error.message.includes('4001')) {
                        toast.error('You rejected the network switch request.');
                        return;
                    }
                    // Same RPC endpoint error
                    if (error.message.includes('same RPC endpoint')) {
                        const targetChain = chains.find(c => c.id === targetChainId);
                        toast.info(`You already have ${targetChain?.name || 'this network'} configured. Please switch to it manually in your wallet.`);
                        return;
                    }
                    toast.error(`Failed to switch network: ${error.message}`);
                },
            }
        );
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors",
                        isUnsupported
                            ? "border-destructive/50 bg-destructive/10 text-destructive hover:bg-destructive/20"
                            : "border-border bg-secondary/50 hover:bg-secondary"
                    )}
                >
                    {isUnsupported ? (
                        <>
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Unsupported Network</span>
                        </>
                    ) : (
                        <>
                            <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
                            <span className="text-sm font-medium">{currentChain?.name || 'Unknown'}</span>
                        </>
                    )}
                    <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    Select Network
                </div>
                {chains.map((chain) => {
                    const isActive = chain.id === chainId;
                    return (
                        <DropdownMenuItem
                            key={chain.id}
                            onClick={() => handleSwitch(chain.id)}
                            disabled={isPending || isActive}
                            className={cn(
                                "flex items-center justify-between cursor-pointer",
                                isActive && "bg-secondary"
                            )}
                        >
                            <div className="flex items-center gap-2">
                                <div
                                    className={cn(
                                        "h-2 w-2 rounded-full",
                                        isActive ? "bg-success" : "bg-muted-foreground/30"
                                    )}
                                />
                                <span>{chain.name}</span>
                            </div>
                            {isActive && <Check className="h-4 w-4 text-primary" />}
                        </DropdownMenuItem>
                    );
                })}
                {isUnsupported && (
                    <div className="px-2 py-2 mt-1 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                            Please switch to a supported network to use FluxSwap
                        </p>
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
